import { useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { Link, useNavigate } from "react-router-dom";
import { tomarRutaPrevia } from "@/lib/api";
import { BandaAmbiente } from "@/components/BandaAmbiente";
import { AMBIENTE_PRODUCTIVO, ETIQUETA_AMBIENTE, URL_PRODUCCION } from "@/lib/ambiente";
import {
  LogIn,
  HelpCircle,
  Phone,
  PlayCircle,
  GraduationCap,
  LifeBuoy,
  ArrowRight,
  ShieldCheck,
} from "lucide-react";

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated) {
      // Si se llegó aquí por un 401, se guardó la ruta en la que estaba el
      // usuario; devolverlo al inicio le hace perder el trabajo en curso.
      navigate(tomarRutaPrevia() ?? "/", { replace: true });
    }
  }, [auth.isAuthenticated, navigate]);

  return (
    <div className="flex min-h-screen flex-col bg-app text-fg">
      <BandaAmbiente />
      {/* Header + hero comparten el fondo "aurora": navy profundo con brillos
          de marca en deriva lenta (CSS puro; ver .login-hero en index.css). */}
      <div className="login-hero text-white">
        <div className="aurora-blob aurora-1" aria-hidden="true" />
        <div className="aurora-blob aurora-2" aria-hidden="true" />
        <div className="aurora-blob aurora-3" aria-hidden="true" />
        {/* Marca de agua institucional: wordmark NPMX en blanco muy tenue. */}
        <img
          src="/assets/logo-bn.png"
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute bottom-6 left-[4%] -z-10 w-[26rem] max-w-[70vw] select-none brightness-0 invert opacity-[0.08]"
        />

        <header>
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
            <div className="flex items-center gap-3">
              <img src="/assets/logo-bn.png" alt="NPMX" className="h-9 w-auto brightness-0 invert" />
              <div className="leading-tight">
                <div className="text-base font-semibold">SICEN</div>
                <div className="text-xs text-white/80">Sistema de Certificaciones · NPMX</div>
              </div>
            </div>
            <div className="hidden text-right text-[11px] text-white/80 sm:block">
              Acreditación EMA 130/17 · Autorización IFT/223/UCS/7706/2025
            </div>
          </div>
        </header>

        {/* HERO asimétrico — mensaje + acción a la izquierda, video a la derecha */}
        <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:py-20">
          <div className="grid items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
                <ShieldCheck className="h-3.5 w-3.5 text-accent" />
                Organismo certificador acreditado EMA · IFT
              </div>

              <h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
                Bienvenido a <span className="text-accent">SICEN</span>
                <br />
                <span className="text-white/70">de NPMX</span>
              </h1>

              <p className="mt-4 max-w-xl text-base leading-relaxed text-white/75">
                Plataforma digital de NPMX para gestionar el ciclo completo de
                certificaciones: alta del Registro Legal, captura de solicitudes por
                sector, evaluación técnica y emisión del certificado oficial.
              </p>

              {!AMBIENTE_PRODUCTIVO && (
                <div
                  role="note"
                  className="mt-5 max-w-xl rounded-lg border border-amber-300/60 bg-amber-400/15 px-4 py-3 text-sm text-amber-50 backdrop-blur-sm"
                >
                  <div className="font-semibold uppercase tracking-wide text-amber-200">
                    {ETIQUETA_AMBIENTE}
                  </div>
                  <p className="mt-1 text-amber-50/90">
                    Estás en el ambiente de pruebas de SICEN. Nada de lo que captures aquí
                    tiene validez oficial. Si vienes a trabajar con información real, entra
                    al{" "}
                    <a href={URL_PRODUCCION} rel="noopener" className="font-semibold underline">
                      sistema productivo
                    </a>
                    .
                  </p>
                </div>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  onClick={() => auth.signinRedirect()}
                  disabled={auth.isLoading}
                  className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 text-sm font-semibold text-npmx-800 shadow-lg shadow-black/20 transition hover:bg-npmx-50 hover:shadow-xl disabled:opacity-50"
                >
                  <LogIn className="h-4 w-4" />
                  {auth.isLoading ? "Cargando…" : "Iniciar sesión"}
                </button>
                <Link
                  to="/ayuda"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/25 px-5 py-3 text-sm font-semibold text-white transition hover:border-white/50 hover:bg-white/10"
                >
                  <HelpCircle className="h-4 w-4" />
                  Centro de Ayuda
                </Link>
              </div>

              {auth.error && (
                <p className="mt-4 max-w-md rounded-md bg-estado-err-bg p-3 text-sm text-estado-err">
                  Error de autenticación: {auth.error.message}
                </p>
              )}
            </div>

            <div className="lg:col-span-6">
              <div className="overflow-hidden rounded-2xl bg-black shadow-2xl shadow-black/40 ring-1 ring-white/15">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  preload="metadata"
                  poster="/assets/logo-NPMX.png"
                  className="block aspect-video w-full bg-black"
                >
                  <source src="/assets/NPMX.mp4" type="video/mp4" />
                  Tu navegador no admite la reproducción de video.
                </video>
              </div>
            </div>
          </div>
        </section>
      </div>

      <main className="flex-1">

        {/* CTAs del Centro de Ayuda con jerarquía: 2 principales + 3 secundarios */}
        <section className="border-t border-edge/60 bg-surface-2/50">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
            <div className="text-center">
              <h2 className="text-2xl font-semibold text-fg">¿Necesitas orientación?</h2>
              <p className="mt-1 text-sm text-fg-muted">
                Consulta el Centro de Ayuda — disponible sin iniciar sesión.
              </p>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <CtaPrincipal
                to="/ayuda?tab=faq"
                icon={<HelpCircle className="h-6 w-6" />}
                titulo="Preguntas frecuentes"
                texto="Recuperar contraseña, qué documentos cargar, formatos aceptados, cómo elegir el esquema correcto y dónde ver el estatus."
              />
              <CtaPrincipal
                to="/ayuda?tab=contacto"
                icon={<Phone className="h-6 w-6" />}
                titulo="Contáctanos"
                texto="Correo institucional, teléfono y dirección de NPMX. Para temas de registro, solicitudes, documentos o soporte general."
              />
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <CtaSecundario
                to="/ayuda?tab=general"
                icon={<PlayCircle className="h-4 w-4" />}
                titulo="Tutorial general"
              />
              <CtaSecundario
                to="/ayuda?tab=tutoriales"
                icon={<GraduationCap className="h-4 w-4" />}
                titulo="Tutoriales por módulo"
              />
              <CtaSecundario
                to="/ayuda?tab=asistencia"
                icon={<LifeBuoy className="h-4 w-4" />}
                titulo="Asistencia personalizada"
              />
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-npmx-700 dark:bg-npmx-950 text-white/85">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-3 px-4 py-5 sm:flex-row sm:justify-between sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/assets/logo-bn.png" alt="NPMX" className="h-8 w-auto brightness-0 invert" />
            <div className="text-[11px] leading-tight">
              <div className="font-semibold text-white">SICEN — Sistema de Certificaciones</div>
              <div>Normalización Profesional de México S.C.</div>
            </div>
          </div>
          <div className="text-center text-[11px] sm:text-right">
            © 2026 · Acreditación EMA 130/17 · Autorización IFT/223/UCS/7706/2025
          </div>
        </div>
      </footer>
    </div>
  );
}

function CtaPrincipal({
  to, icon, titulo, texto,
}: {
  to: string;
  icon: React.ReactNode;
  titulo: string;
  texto: string;
}) {
  return (
    <Link
      to={to}
      className="group flex flex-col rounded-2xl border border-edge bg-surface p-6 shadow-card transition hover:-translate-y-0.5 hover:border-primary-strong hover:shadow-card-hover"
    >
      <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary-tint text-primary-strong">
        {icon}
      </span>
      <h3 className="mt-4 text-lg font-semibold text-fg">{titulo}</h3>
      <p className="mt-1 flex-1 text-sm text-fg-muted">{texto}</p>
      <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-strong">
        Abrir <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
      </div>
    </Link>
  );
}

function CtaSecundario({
  to, icon, titulo,
}: {
  to: string;
  icon: React.ReactNode;
  titulo: string;
}) {
  return (
    <Link
      to={to}
      className="group flex items-center gap-2 rounded-lg border border-edge bg-surface px-4 py-3 text-sm text-fg-muted transition hover:border-primary-strong hover:text-primary-strong"
    >
      <span className="grid h-7 w-7 place-items-center rounded-md bg-surface-2 text-fg-subtle transition group-hover:bg-npmx-700 dark:bg-npmx-950/10 group-hover:text-primary-strong">
        {icon}
      </span>
      <span className="flex-1 font-medium">{titulo}</span>
      <ArrowRight className="h-3.5 w-3.5 opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
    </Link>
  );
}
